import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import Keycloak from 'keycloak-js';
import { firstValueFrom, timeout } from 'rxjs';

/** Timeout für Admin-API Requests in Millisekunden */
const API_TIMEOUT = 15000;

/** Minimale Token-Gültigkeit in Sekunden - Token wird refreshed wenn weniger übrig */
const MIN_TOKEN_VALIDITY = 30;

export interface KeycloakUser {
	id: string;
	username: string;
	email?: string;
	firstName?: string;
	lastName?: string;
	enabled: boolean;
	emailVerified?: boolean;
	createdTimestamp?: number;
}

export interface KeycloakRole {
	id: string;
	name: string;
	description?: string;
	composite?: boolean;
	children?: KeycloakRole[];
}

export interface KeycloakClient {
	id: string;
	clientId: string;
	name?: string;
}

@Injectable({
	providedIn: 'root'
})
export class KeycloakAdminService {
	private readonly keycloak = inject(Keycloak);
	private readonly http = inject(HttpClient);

	private get keycloakUrl(): string {
		return this.keycloak.authServerUrl ?? '';
	}

	private get realm(): string {
		return this.keycloak.realm ?? '';
	}

	/**
	 * Stellt sicher, dass das Token gültig ist und gibt die Headers zurück.
	 * Nutzt manuellen Token-Refresh als Fallback wenn keycloak-js updateToken versagt.
	 */
	private async getHeaders(): Promise<HttpHeaders> {
		// isTokenExpired wirft einen String ('Not authenticated.') wenn tokenParsed undefined ist
		// (z.B. nach Auto-Logout durch keycloak-angular sessionTimeout)
		let needsRefresh = false;
		try {
			needsRefresh = !!this.keycloak.isTokenExpired?.(MIN_TOKEN_VALIDITY);
		} catch {
			needsRefresh = true;
		}

		if (needsRefresh || !this.keycloak.token) {
			// Zuerst keycloak-js updateToken versuchen
			let refreshed = false;
			try {
				await this.keycloak.updateToken(MIN_TOKEN_VALIDITY);
				refreshed = !!this.keycloak.token && !this.keycloak.isTokenExpired?.(MIN_TOKEN_VALIDITY);
			} catch { /* Fallback unten */ }

			// Fallback: Manueller Refresh über Token-Endpoint
			if (!refreshed) {
				const manualOk = await this.refreshTokenManually();
				if (!manualOk) {
					throw new Error('Anmeldung abgelaufen - bitte neu anmelden');
				}
			}
		}

		const token = this.keycloak.token;
		if (!token) {
			throw new Error('Kein gültiges Anmeldetoken vorhanden');
		}

		return new HttpHeaders({
			'Authorization': `Bearer ${token}`,
			'Content-Type': 'application/json'
		});
	}

	/**
	 * Manueller Token-Refresh über den Keycloak-Token-Endpoint.
	 * Funktioniert unabhängig von keycloak-js internem State (z.B. nach loginDirect).
	 */
	private async refreshTokenManually(): Promise<boolean> {
		const refreshToken = this.keycloak.refreshToken;
		if (!refreshToken) return false;

		const tokenUrl = `${this.keycloakUrl}/realms/${this.realm}/protocol/openid-connect/token`;
		try {
			const response = await fetch(tokenUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
				body: new URLSearchParams({
					grant_type: 'refresh_token',
					client_id: this.keycloak.clientId ?? 'bpweb',
					refresh_token: refreshToken
				})
			});

			if (!response.ok) return false;

			const data = await response.json();

			// Tokens auf Keycloak-Instanz setzen
			const kc = this.keycloak as any;
			kc.token = data.access_token;
			kc.refreshToken = data.refresh_token;
			if (data.id_token) kc.idToken = data.id_token;

			// Token parsen
			const parts = (data.access_token as string).split('.');
			if (parts.length === 3) {
				kc.tokenParsed = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(parts[1]), c => c.charCodeAt(0))));
			}

			return true;
		} catch {
			return false;
		}
	}

	async getUsers(max = 100, first = 0): Promise<KeycloakUser[]> {
		const headers = await this.getHeaders();
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users?max=${max}&first=${first}`;
		return firstValueFrom(this.http.get<KeycloakUser[]>(url, { headers }).pipe(timeout(API_TIMEOUT)));
	}

	async searchUsers(search: string, max = 20): Promise<KeycloakUser[]> {
		const headers = await this.getHeaders();
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users?search=${encodeURIComponent(search)}&max=${max}`;
		return firstValueFrom(this.http.get<KeycloakUser[]>(url, { headers }).pipe(timeout(API_TIMEOUT)));
	}

	async getUserRoles(userId: string): Promise<KeycloakRole[]> {
		const headers = await this.getHeaders();
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users/${userId}/role-mappings/realm`;
		return firstValueFrom(this.http.get<KeycloakRole[]>(url, { headers }).pipe(timeout(API_TIMEOUT)));
	}

	async getUserEffectiveRoles(userId: string): Promise<KeycloakRole[]> {
		const headers = await this.getHeaders();
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users/${userId}/role-mappings/realm/composite`;
		return firstValueFrom(this.http.get<KeycloakRole[]>(url, { headers }).pipe(timeout(API_TIMEOUT)));
	}

	async getRealmRoles(): Promise<KeycloakRole[]> {
		const headers = await this.getHeaders();
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/roles`;
		return firstValueFrom(this.http.get<KeycloakRole[]>(url, { headers }).pipe(timeout(API_TIMEOUT)));
	}

	async getCompositeRoleChildren(roleName: string): Promise<KeycloakRole[]> {
		const headers = await this.getHeaders();
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/roles/${encodeURIComponent(roleName)}/composites`;
		return firstValueFrom(this.http.get<KeycloakRole[]>(url, { headers }).pipe(timeout(API_TIMEOUT)));
	}

	async getUserRolesExpanded(userId: string): Promise<KeycloakRole[]> {
		const roles = await this.getUserRoles(userId);
		for (const role of roles) {
			if (role.composite) {
				role.children = await this.getCompositeRoleChildren(role.name);
			}
		}
		return roles;
	}

	async getClientByClientId(clientId: string): Promise<KeycloakClient | undefined> {
		const headers = await this.getHeaders();
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/clients?clientId=${encodeURIComponent(clientId)}`;
		const clients = await firstValueFrom(this.http.get<KeycloakClient[]>(url, { headers }).pipe(timeout(API_TIMEOUT)));
		return clients?.[0];
	}

	async getUserClientRoles(userId: string, clientUuid: string): Promise<KeycloakRole[]> {
		const headers = await this.getHeaders();
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users/${userId}/role-mappings/clients/${clientUuid}`;
		return firstValueFrom(this.http.get<KeycloakRole[]>(url, { headers }).pipe(timeout(API_TIMEOUT)));
	}

	async getUserClientEffectiveRoles(userId: string, clientUuid: string): Promise<KeycloakRole[]> {
		const headers = await this.getHeaders();
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users/${userId}/role-mappings/clients/${clientUuid}/composite`;
		return firstValueFrom(this.http.get<KeycloakRole[]>(url, { headers }).pipe(timeout(API_TIMEOUT)));
	}

	async getClientCompositeRoleChildren(clientUuid: string, roleName: string): Promise<KeycloakRole[]> {
		const headers = await this.getHeaders();
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/clients/${clientUuid}/roles/${encodeURIComponent(roleName)}/composites`;
		return firstValueFrom(this.http.get<KeycloakRole[]>(url, { headers }).pipe(timeout(API_TIMEOUT)));
	}

	async getUserClientRolesExpanded(userId: string, clientId: string): Promise<KeycloakRole[]> {
		const client = await this.getClientByClientId(clientId);
		if (!client) return [];

		// Get direct roles (for showing which are directly assigned)
		const directRoles = await this.getUserClientRoles(userId, client.id);
		const directRoleNames = new Set(directRoles.map(r => r.name));

		// Get all effective roles (includes inherited)
		const effectiveRoles = await this.getUserClientEffectiveRoles(userId, client.id);

		// Build result: direct roles with children expanded, plus mark inherited
		for (const role of directRoles) {
			if (role.composite) {
				role.children = await this.getClientCompositeRoleChildren(client.id, role.name);
			}
		}

		// Add effective roles that are not direct (inherited from realm roles)
		const inheritedRoles = effectiveRoles.filter(r => !directRoleNames.has(r.name));

		return [...directRoles, ...inheritedRoles];
	}
}
